import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import moment from 'jalali-moment';
import { toast } from "react-toastify";

import { commafy, decommafy, swal, toEnglishDigits, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ConfirmModal from "../../../components/Modals/ConfirmModal/ConfirmModal.component";

const UsersEdit = () => {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('users'), link: '/users-list' },
		{ title: t('edit') }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [userCategories, setUserCategories] = useState([]);
	const getUserCategories = async () => {
		let body = {
			usercategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/usercategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setUserCategories(response.data.usercategories.map(item => {
					return {
						key: item.usercategory_name,
						value: item.usercategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createUser = event => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let fullname = event.target.userFullname.value.trim();
		if(!typeChecker(fullname, 'title'))
			return toast.warn(t('fullname') + ' ' + t('must_have_at_least_3_characters'));
		let is_active = event.target.userIsActive.checked ? 1 : 0;
		let mobile = event.target.userMobile.value.trim();
		// if(!typeChecker(mobile, 'mobile'))
		// 	return toast.warn(t('enter_mobile_number_correctly'));
		let nationalID = event.target.userNationalID.value.trim();
		// if(!typeChecker(nationalID, 'national_id'))
		// 	return toast.warn(t('enter_national_id_correctly'));
		let tradeCode = event.target.userTradeCode.value.trim();
		if(!typeChecker(tradeCode, 'number'))
			return toast.warn(t('enter_trade_code_correctly'));
		let categoryID = event.target.userCategory.value ? event.target.userCategory.value : null;
		let inviterCode = event.target.userInviterCode.value.trim();
		let allowedCodes = [];
		document.querySelectorAll('[data-form-field-multi-item="userAllowedCodeTitle"]').forEach(item => {
			let code = item.querySelector('[data-form-field-multi-item-input="userAllowedCodeTitle"]').value.trim();
			if(!typeChecker(code, 'title'))
				return toast.warn(t('code') + ' ' + t('must_have_at_least_3_characters'));
			let commission = item.querySelector('[data-form-field-multi-item-input="userAllowedCodeCommission"]').value.trim();
			if(!typeChecker(commission, 'number'))
				return toast.warn(t('enter_commission_percentage_correctly'));
			allowedCodes.push({
				code,
				commission
			});
		});
		let prxCodes = [];
		document.querySelectorAll('[data-form-field-multi-item="userPrxCodeTradeCode"]').forEach(item => {
			let tradeCode = item.querySelector('[data-form-field-multi-item-input="userPrxCodeTradeCode"]').value.trim();
			if(!typeChecker(tradeCode, 'number'))
				return toast.warn(t('enter_trade_code_correctly'));
			prxCodes.push({
				trade_code: tradeCode
			});
		});
		let isMainAccount = event.target.userIsMainAccount.checked ? 1 : 0;
		let isZarFormulaActive = event.target.userIsZarFormulaActive.checked;
		let body = {
			user_id: id,
			user_fullname: fullname,
			is_active,
			phone_number: mobile,
			national_code: nationalID,
			usercategory_id: categoryID,
			other_information: {
				...(typeof user?.other_information === 'object' ? user.other_information : {}),
				allowed_invite_codes: allowedCodes,
				prx_trade_codes: prxCodes
			},
			invite_code: user?.invite_code,
			inviter_code: inviterCode,
			trade_code: tradeCode,
			authentication_info: user?.authentication_info,
			is_credit: user?.is_credit,
			extra_data: user?.extra_data,
			login_flag: isMainAccount,
			is_zar_formula_active: isZarFormulaActive
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/users/admin/edit', body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [updateUserCreditButtonIsLoading, setUpdateUserCreditButtonIsLoading] = useState(false);
	const updateUserCredit = event => {
		event.preventDefault();
		if(updateUserCreditButtonIsLoading)
			return 0;
		// let onlinePermanent = parseInt(toEnglishDigits(decommafy(event.target.userOnlinePermanent.value.trim())));
		// if(!typeChecker(onlinePermanent, 'number'))
		// 	return toast.warn(t('enter_online_permanent_correctly'));
		// let onlineDaily = parseInt(toEnglishDigits(decommafy(event.target.userOnlineDaily.value.trim())));
		// if(!typeChecker(onlineDaily, 'number'))
		// 	return toast.warn(t('enter_online_daily_correctly'));
		// let stationPermanent = parseInt(toEnglishDigits(decommafy(event.target.userStationPermanent.value.trim())));
		// if(!typeChecker(stationPermanent, 'number'))
		// 	return toast.warn(t('enter_station_permanent_correctly'));
		// let stationDaily = parseInt(toEnglishDigits(decommafy(event.target.userStationDaily.value.trim())));
		// if(!typeChecker(stationDaily, 'number'))
		// 	return toast.warn(t('enter_station_daily_correctly'));
		let status = event.target.userCreditStatus.value.trim();
		let body = {
			user_id: id,
			user_all_time_credit: user.user_all_time_credit,
			user_daily_credit: user.user_daily_credit,
			user_all_time_credit_contract_value: user.user_all_time_credit_contract_value,
			user_daily_credit_contract_value: user.user_daily_credit_contract_value,
			contract_status: status
		};
		confirmSwal(async () => {
			setUpdateUserCreditButtonIsLoading(true);
			let response = await _post('/users/admin/editCredit', body);
			setUpdateUserCreditButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	const [user, setUser] = useState({});
	const getUser = async () => {
		let body = {
			user_id: id
		};
		let response = await _post('/users/admin/getOne', body);
		switch(response.code){
			case 200:
				setUser(response.data.user);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const userAllowedCodesMultipleOptions = {
		keys: [
			"userAllowedCodeTitle",
			"userAllowedCodeID",
			"userAllowedCodeCommission"
		],
		titles: [
			t('code'),
			t('code_id'),
			t('commission_percentage')
		],
		foreignKey: {
			api: '/codes/admin/getAll',
			body: {
				page: 1,
				count: 10
			},
			response_key: 'codes',
			key: 'code_id',
			title: ['code_name'],
			searchKey: 'code_name',
			searchTitle: t('code')
		},
		directions: ['rtl', 'lrt', 'ltr']
	};

	const userPrxCodesMultipleOptions = {
		keys: [
			"userPrxCodeTradeCode",
		],
		titles: [
			t('trade_code')
		],
		foreignKey: null,
		directions: ['ltr']
	};

	useEffect(() => {
		const fetchData = async () => {
			await getUserCategories();
			getUser();
		}
		fetchData();
	}, []);

	const [deleteButtonIsLoading, setDeleteButtonIsLoading] = useState(false);
	const deleteUser = async () => {
		if(deleteButtonIsLoading)
			return 0;
		let body = {
			user_id: id
		};
		confirmSwal(async () => {
			setDeleteButtonIsLoading(true); 
			let response = await _post('/users/admin/delete', body);
			setDeleteButtonIsLoading(false);
			switch(response.code){
				case 200:
					navigate("/users-list");
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	};

	return <>
			<Breadcrumb items={breadcrumbItems} />
		<div className="card mb-3">
			<div className="card-header">
				<h5 className="mb-0">{t('edit')} {t('user')}</h5>
			</div>
			<div className="card-body bg-light">
				<form onSubmit={createUser}>
					<div className="row gx-2">
						<FormField fieldKey="userFullname" title={t('fullname')} value={user?.user_fullname} required rtl />
						<FormField type="toggle" fieldKey="userIsActive" title={t('active')} value={user?.is_active} required />
						<FormField fieldKey="userMobile" title={t('mobile')} value={user?.phone_number} ltr />
						<FormField fieldKey="userNationalID" title={t('national_id')} value={user?.national_code} ltr />
						<FormField type="number" fieldKey="userRialBalance" title={t('score1_name')} value={commafy(user?.user_rial_wallet)} disabled ltr />
						<FormField type="number" fieldKey="userScoreBalance" title={t('score2_name')} value={commafy(user?.user_star_wallet)} disabled ltr />
						<FormField type="select" fieldKey="userCategory" title={t('category')} value={user?.usercategory_id} selectItems={userCategories} rtl />
						<FormField fieldKey="userInviterCode" title={t('inviter_code')} value={user?.inviter_code} ltr />
						<FormField type="multi" fieldKey="userAllowedCodes" title={t('allowed_codes')} multiSettings={userAllowedCodesMultipleOptions} value={JSON.stringify(user?.other_information?.allowed_invite_codes?.map(code => code.code + ',' + '0000' + ',' + code.commission))} />
						<FormField type="multi" fieldKey="userPrxCodes" title={t('prx_trade_codes')} multiSettings={userPrxCodesMultipleOptions} value={JSON.stringify(user?.other_information?.prx_trade_codes?.map(item => item.trade_code))} />
						<FormField fieldKey="userRegisteryDate" title={t('registery_date')} value={user?.create_date ? moment(user?.create_date?.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD') : ''} disabled ltr />
						<FormField fieldKey="userTradeCode" title={t('trade_code')} value={user?.trade_code} required ltr />
						<FormField type="toggle" fieldKey="userIsMainAccount" title={t('main-account')} value={user?.login_flag} />
						<FormField type="toggle" fieldKey="userIsZarFormulaActive" title={t('zar_formula')} value={user?.is_zar_formula_active} />
					</div>
					<div>
						<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
							<i className="fa fa-save"></i> {t('update')}
						</LoadingButton>
					</div>
				</form>
			</div>
		</div>
		<div className="card mb-3">
			<div className="card-header">
				<h5 className="mb-0">{t('edit')} {t('user_credits')}</h5>
			</div>
			<div className="card-body bg-light">
				<form onSubmit={updateUserCredit}>
					<div className="row gx-2">
						<FormField type="number" fieldKey="userOnlinePermanent" title={t('online_permanent')} value={commafy(user?.user_all_time_credit)} disabled ltr />
						<FormField type="number" fieldKey="userOnlineDaily" title={t('online_daily')} value={commafy(user?.user_daily_credit)} disabled ltr />
						<FormField type="number" fieldKey="userOnlineTotal" title={t('online_total')} value={commafy(user?.user_credit)} disabled ltr />
						<FormField type="number" fieldKey="userStationTotal" title={t('station_total')} value={commafy(user?.user_credit_contract_value)} disabled ltr />
						<FormField type="number" fieldKey="userStationPermanent" title={t('station_permanent')} value={commafy(user?.user_all_time_credit_contract_value)} disabled ltr />
						<FormField type="number" fieldKey="userStationDaily" title={t('station_daily')} value={commafy(user?.user_daily_credit_contract_value)} disabled ltr />
						<FormField type="text" fieldKey="userCreditStatus" title={t('status')} value={user?.contract_status} rtl />
						<FormField type="text" fieldKey="userCreditDescription" title={t('description')} value={user?.credit_description} disabled rtl />
					</div>
					<div>
						<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={updateUserCreditButtonIsLoading}>
							<i className="fa fa-save"></i> {t('update')}
						</LoadingButton>
					</div>
				</form>
			</div>
		</div>
        <ConfirmModal action={createUser} />
	</>;
};

export default UsersEdit;
